import ShopifyLineItem from '@grafikr/shopify-typescript/type/LineItem';
import { image } from 'utils/image';
import Cart, { LineItem } from '../../typescript/Cart';
import formatMoney from './format-money';

type Variant = {
  id: number
  quantity: number
  compare_at_price: number | null
  tags: string[]
};

type Items = {
  items: Variant[]
};

export type Config = {
  lineItemCounters?: HTMLElement[]
  subtotalDisplay: HTMLElement
  savingsDisplay: HTMLElement
  totalDisplay: HTMLElement
  shippingDisplay: HTMLElement
  lineItemListElement: HTMLElement
  submitCheckoutButton: HTMLButtonElement
  termsCheckbox: HTMLInputElement
  shippingBarStatus: HTMLParagraphElement | null
  shippingProgressbar: HTMLElement | null
  shippingThreshold: number | null
};

class CartHandler {
  public cartObject:Cart;

  public readonly config:Config;

  constructor(config: Config) {
    this.cartObject = window.theme.cart;
    this.config = { ...config };
  }

  attachListeners(lineItem: HTMLElement) {
    const lineItemKey = lineItem.dataset.lineitem;
    const increaseQuantity = lineItem.querySelector('[data-increase]');
    const decreaseQuantity = lineItem.querySelector('[data-decrease]');
    const removeButton = lineItem.querySelector('[data-remove]');

    increaseQuantity.addEventListener('click', () => this.updateQuantity(1, lineItemKey));
    decreaseQuantity.addEventListener('click', () => this.updateQuantity(-1, lineItemKey));
    removeButton.addEventListener('click', () => this.removeLineItem(lineItemKey));
  }

  removeLineItem(lineItemKey) {
    this.changeLineItem(lineItemKey, { quantity: 0 });
  }

  updateQuantity(amount:number, lineItemKey: string) {
    const cartLineItem = this.cartObject.items.find((item) => item.key === lineItemKey);
    this.changeLineItem(lineItemKey, { quantity: cartLineItem.quantity + amount });
  }

  async addVariant(variants:Items) {
    const previousCartState = structuredClone(this.cartObject);
    const body = {
      items: variants.items.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      })),
    };

    // If line item doesn't exist in our cartObject we render a placeholder
    variants.items.forEach((item) => {
      const variantAlreadyInCart = this.cartObject.items.find(
        (lineItem) => lineItem.variant.id === item.id,
      );
      if (!variantAlreadyInCart) {
        const placeholderAlreadyExists = this.config.lineItemListElement.querySelector(`[data-lineitem="pl-${item.id}-${item.quantity}"]`);
        if (!placeholderAlreadyExists) {
          let placeholderHTML = theme.templates.lineItemPlaceholder;
          placeholderHTML = placeholderHTML.replace('[%key%]', `pl-${item.id}-${item.quantity}`);
          this.config.lineItemListElement.insertAdjacentHTML('afterbegin', placeholderHTML);
        }
      }
    });

    document.dispatchEvent(new CustomEvent('cart:state:updated', { detail: 1 }));

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.status === 200) return response.json();
        if (response.status === 422) {
          throw new Error('All products are already in your cart');
        }
        throw new Error('Could not add product to cart');
      })
      .then((data) => {
        this.addToCartObject(data, variants);
        data.items.forEach((item) => {
          // If the line-item already existed in this.cartObject prior to fetch,
          // update the line-item instead of creating a new one.
          const foundLineItem = previousCartState.items.find(
            (lineItem) => lineItem.key === item.key,
          );
          if (foundLineItem) {
            this.updateLineItem(item.key);
          } else {
            const getLineItem = this.cartObject.items.find((lineItem) => lineItem.key === item.key);
            this.createLineItemElement(getLineItem);
            const findPlaceholder = this.config.lineItemListElement.querySelector(`[data-lineitem="${`pl-${item.id}-${item.quantity}`}"]`);
            if (findPlaceholder) findPlaceholder.remove();
          }
        });
      })
      .catch((error) => console.error(error)); // eslint-disable-line no-console
  }

  async changeLineItem(lineItemKey, options) {
    const body = {
      updates: {
        [lineItemKey]: options.quantity,
      },
    };

    const overlay = this.config.lineItemListElement.querySelector(`[data-lineitem="${lineItemKey}"] [data-overlay]`);
    overlay.setAttribute('data-active', 'true');

    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.status === 200) return response.json();
        throw new Error('Could not update');
      })
      .then((data) => {
        this.updateCartObject(lineItemKey, data);
        this.updateLineItem(lineItemKey);
        overlay.setAttribute('data-active', 'false');
      })
      .catch((error) => console.error(error)); // eslint-disable-line no-console
  }

  updateLineItem(lineItemKey) {
    const cartLineItem = this.cartObject.items.find((item) => item.key === lineItemKey);
    const lineItemElement = this.config.lineItemListElement.querySelector(`[data-lineitem="${lineItemKey}"]`);

    if (cartLineItem === undefined) {
      lineItemElement.remove();
      return;
    }

    const quantity = lineItemElement.querySelector('[data-quantity]');
    quantity.innerHTML = `${cartLineItem.quantity}`;

    const currentPrice = lineItemElement.querySelector('[data-price]');
    currentPrice.innerHTML = `${formatMoney(cartLineItem.price * cartLineItem.quantity)}`;

    const compareAtPrice = lineItemElement.querySelector('[data-compare-at-price]');
    if (compareAtPrice !== null) {
      compareAtPrice.innerHTML = `${formatMoney(cartLineItem.variant.compare_at_price * cartLineItem.quantity)}`;
    }
  }

  createLineItemElement(lineItem:LineItem) {
    let element = theme.templates.lineItem;
    element = element.replace('[%key%]', lineItem.key);
    element = element.replaceAll('[%url%]', lineItem.url);

    if (lineItem.image.url !== '' || lineItem.image.url !== undefined) {
      element = element.replace('[%image%]', image(lineItem.image));
    } else {
      element = element.replace('[%image%]', '<div class="aspect-ratio aspect-ratio--1/1 bg-neutral-200"></div>');
    }

    element = element.replace('[%title%]', lineItem.title);
    if (lineItem.variant.compare_at_price !== null && lineItem.variant.compare_at_price !== 0) {
      element = element.replace('[%compare_at_price%]', `<del class="text-sm font-medium text-neutral-500" data-compare-at-price>${formatMoney(lineItem.variant.compare_at_price * lineItem.quantity)}</del>`);
    } else {
      element = element.replace('[%compare_at_price%]', '');
    }
    element = element.replace('[%final_price%]', formatMoney(lineItem.price * lineItem.quantity).toString());
    element = element.replace('[%quantity%]', lineItem.quantity.toString());

    this.config.lineItemListElement.insertAdjacentHTML('afterbegin', element);
    const htmlElement:HTMLElement = this.config.lineItemListElement.querySelector(`[data-lineitem="${lineItem.key}"]`);
    this.attachListeners(htmlElement);
  }

  calculateSavings() {
    let savings = 0;

    this.cartObject.items.forEach((lineItem) => {
      if (lineItem.variant.compare_at_price) {
        savings += (lineItem.variant.compare_at_price - lineItem.price) * lineItem.quantity;
      }
    });

    return savings;
  }

  calculateSubtotal() {
    let subtotal = 0;

    this.cartObject.items.forEach((lineItem) => {
      if (lineItem.variant.compare_at_price) {
        subtotal += lineItem.variant.compare_at_price * lineItem.quantity;
      } else {
        subtotal += lineItem.price * lineItem.quantity;
      }
    });

    return subtotal;
  }

  calculateTotal() {
    let total = 0;

    this.cartObject.items.forEach((lineItem) => {
      total += lineItem.price * lineItem.quantity;
    });

    return total;
  }

  calculateItemCount() {
    let count = 0;

    this.cartObject.items.forEach((lineItem) => {
      count += lineItem.quantity;
    });

    return count;
  }

  addToCartObject(data, variants: Items) {
    const { items } = data;
    items.forEach((lineItem:ShopifyLineItem) => {
      const test = this.cartObject.items.find((item) => lineItem.key === item.key);

      if (test !== undefined) {
        test.quantity = lineItem.quantity;
      } else {
        const variant = variants.items.find((item) => item.id === lineItem.id);
        const newLineItem:LineItem = {
          id: lineItem.id,
          image: {
            width: lineItem.featured_image.width,
            height: lineItem.featured_image.height,
            url: lineItem.featured_image.url,
            alt: lineItem.featured_image.alt,
          },
          key: lineItem.key,
          price: lineItem.final_price,
          quantity: lineItem.quantity,
          title: lineItem.title,
          url: lineItem.url,
          variant: {
            compare_at_price: variant.compare_at_price,
            id: lineItem.variant_id,
            title: lineItem.variant_title,
          },
          tags: variant.tags,
        };
        this.cartObject.items.unshift(newLineItem);
      }
    });
    this.cartObject.savings = this.calculateSavings();
    this.cartObject.subtotal = this.calculateSubtotal();
    this.cartObject.item_count = this.calculateItemCount();
    this.cartObject.cart_total = this.calculateTotal();
    this.updateCartState();
    document.dispatchEvent(new Event('cart:updated'));
  }

  updateCartObject(lineItemKey, data) {
    const lineItemAlreadyExists = data.items.find((item) => item.key === lineItemKey);
    if (lineItemAlreadyExists) {
      const cartLineItem = this.cartObject.items.find((item) => item.key === lineItemKey);
      cartLineItem.quantity = lineItemAlreadyExists.quantity;
    } else {
      this.cartObject.items = this.cartObject.items.filter((item) => item.key !== lineItemKey);
    }
    this.cartObject.savings = this.calculateSavings();
    this.cartObject.subtotal = this.calculateSubtotal();
    this.cartObject.item_count = this.calculateItemCount();
    this.cartObject.cart_total = this.calculateTotal();
    this.updateCartState();
    document.dispatchEvent(new Event('cart:updated'));
  }

  calculateFreeShippingProgress() {
    if (this.config.shippingBarStatus === null && this.config.shippingProgressbar === null) {
      return;
    }

    const { shipping: shippingTranslations } = window.theme.strings.cart;
    const thresholdPercentage = (this.cartObject.cart_total / this.config.shippingThreshold) * 100;
    const thresholdPercentageClamped = Math.min(Math.max(thresholdPercentage, 0), 100);

    this.config.shippingProgressbar.style.setProperty('--progress', `${thresholdPercentageClamped}%`);

    if (this.cartObject.cart_total < this.config.shippingThreshold) {
      const thresholdRemainder = this.config.shippingThreshold - this.cartObject.cart_total;
      const statusString = shippingTranslations.threshold_not_reached.replace(
        '{{ threshold }}',
        formatMoney(thresholdRemainder).toString(),
      );
      this.config.shippingBarStatus.innerText = statusString;
      this.config.shippingDisplay.innerText = shippingTranslations.shipping_tbd;
      // this.config.shippingDisplay.parentElement.hidden = true;
    } else {
      this.config.shippingBarStatus.innerText = shippingTranslations.threshold_reached;
      this.config.shippingDisplay.innerText = shippingTranslations.shipping_free;
      // this.config.shippingDisplay.parentElement.hidden = false;
    }
  }

  updateCartState() {
    if (this.config.lineItemCounters) {
      this.config.lineItemCounters.forEach((counter) => {
        counter.innerHTML = this.cartObject.item_count.toString();
      });
    }

    this.calculateFreeShippingProgress();

    this.config.totalDisplay.innerText = formatMoney(this.cartObject.cart_total).toString();
    this.config.subtotalDisplay.innerText = formatMoney(this.cartObject.subtotal).toString();

    if (this.cartObject.savings === 0) {
      this.config.savingsDisplay.parentElement.hidden = true;
    } else {
      this.config.savingsDisplay.parentElement.hidden = false;
    }

    this.config.savingsDisplay.innerText = formatMoney(this.cartObject.savings).toString();

    if (this.cartObject.item_count > 0) {
      this.config.submitCheckoutButton.disabled = false;
    } else {
      this.config.submitCheckoutButton.disabled = true;
    }

    document.dispatchEvent(new CustomEvent('cart:state:updated', { detail: this.cartObject.item_count }));
  }

  checkTerms(e) {
    e.preventDefault();
    if (this.config.termsCheckbox.checked) {
      window.location.href = '/checkout';
    } else {
      document.dispatchEvent(new Event('cart:terms:open'));
    }
  }

  init() {
    const lineItems:NodeListOf<HTMLElement> = this.config.lineItemListElement.querySelectorAll('[data-lineitem]');
    lineItems.forEach((lineItem) => this.attachListeners(lineItem));
    this.config.submitCheckoutButton.addEventListener('click', (e) => this.checkTerms(e));
    this.calculateFreeShippingProgress();
  }
}

export default CartHandler;
